// Cost guardrails — NEW console page (GAP-3, 18G §2.4 "Cost guardrails
// (storage + egress incl. embed-view aggregates)", D-21). Reads the same
// D-33 daily rollup mirror as TrustOrgMetricsPage (GET /trust/org-metrics —
// no new backend route needed, this is purely a focused re-presentation),
// surfacing embed-view volume, parse-credit consumption, and storage/egress
// against a soft per-tier guardrail so an operator can see who's trending
// toward a real quota conversation before it becomes one.
//
// storage_mb/egress_mb are additive fields of the schema_version=1 rollup
// (types/trust.ts). TPRM7's trust-telemetry-rollup has sent both on every org
// row since OF-078 (2026-10-02) — storage_mb is the evidence held at the end of
// the UTC day, egress_mb the bytes the public trust functions produced that day.
// An org whose usage read failed sends NEITHER (never 0: a zero claims it used
// nothing), and an org that has not yet pushed since the rollup shipped has no
// value either. This page renders both honestly (a "not yet reported" dark
// state), the same convention the pricing/ page uses for TODO price rows — it
// does not fabricate a number. The platform-wide daily totals also reach
// CloudWatch through the backend's `trust_funnel` log line.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { RefreshCw, Eye, HardDrive, Ticket, ShieldAlert } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { Kpi, UtilBar } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { AttnRow } from '@/components/shared/AttnRow';
import { Pill } from '@/components/shared/badges';
import { getOrgMetrics, getSaasDetails, orgNameFor, tierLabel, type TrustOrgMetricsRow, type SaasLicenseTier } from '@/lib/api/trust';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5 };

// Soft guardrail caps (18H §4.2 "per-org quota bars"). PLACEHOLDER values —
// no rate card exists yet (same open item the opportunity ledger's
// value_estimate is pending, 17_ §9 item 13). Storage in MB, embed views/day.
// Rendered with an explicit "placeholder" hint, never presented as billed
// enforcement — T1 is visibility only, per D-21.
const STORAGE_SOFT_CAP_MB: Record<SaasLicenseTier, number> = {
  free: 250,
  trust_base: 1000,
  trust_pro: 5000,
  enterprise: 25000,
};
const EMBED_VIEWS_SOFT_CAP: Record<SaasLicenseTier, number> = {
  free: 2000,
  trust_base: 20000,
  trust_pro: 100000,
  enterprise: 1000000,
};

type SortMetric = 'embed_views' | 'parse_consumed' | 'storage_mb';
const SORT_LABEL: Record<SortMetric, string> = {
  embed_views: 'Embed views',
  parse_consumed: 'Parse credits consumed',
  storage_mb: 'Storage (MB)',
};

export function CostGuardrailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const base = personaBase(location.pathname);
  const [rows, setRows] = useState<TrustOrgMetricsRow[]>([]);
  const [tierByLicense, setTierByLicense] = useState<Record<string, SaasLicenseTier>>({});
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sortMetric, setSortMetric] = useState<SortMetric>('embed_views');

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getOrgMetrics();
      setRows(data || []);
      // Secondary fetch (tier, for guardrail caps + labels) — a failure here
      // degrades caps to the 'free' default rather than nuking the page.
      try {
        const details = await getSaasDetails();
        const map: Record<string, SaasLicenseTier> = {};
        for (const d of details) map[d.license_id] = d.tier;
        setTierByLicense(map);
      } catch {
        setTierByLicense({});
      }
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tierFor = useCallback((r: TrustOrgMetricsRow): SaasLicenseTier => tierByLicense[r.license_id] || 'free', [tierByLicense]);

  const reportingStorage = useMemo(() => rows.filter((r) => typeof r.metrics.storage_mb === 'number'), [rows]);
  const reportingEgress = useMemo(() => rows.filter((r) => typeof r.metrics.egress_mb === 'number'), [rows]);

  const overGuardrail = useMemo(() => rows.filter((r) => {
    const tier = tierFor(r);
    const storageOver = typeof r.metrics.storage_mb === 'number' && r.metrics.storage_mb >= STORAGE_SOFT_CAP_MB[tier] * 0.9;
    const embedOver = r.metrics.embed_views >= EMBED_VIEWS_SOFT_CAP[tier] * 0.9;
    return storageOver || embedOver;
  }), [rows, tierFor]);

  const kpis = useMemo(() => {
    const totalEmbedViews = rows.reduce((s, r) => s + (r.metrics.embed_views || 0), 0);
    const totalParseConsumed = rows.reduce((s, r) => s + (r.metrics.parse_consumed || 0), 0);
    const totalStorage = reportingStorage.reduce((s, r) => s + (r.metrics.storage_mb || 0), 0);
    return { totalEmbedViews, totalParseConsumed, totalStorage, overCount: overGuardrail.length };
  }, [rows, reportingStorage, overGuardrail]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = (sortMetric === 'embed_views' ? a.metrics.embed_views : sortMetric === 'parse_consumed' ? a.metrics.parse_consumed : a.metrics.storage_mb) ?? -1;
      const bv = (sortMetric === 'embed_views' ? b.metrics.embed_views : sortMetric === 'parse_consumed' ? b.metrics.parse_consumed : b.metrics.storage_mb) ?? -1;
      return bv - av;
    });
  }, [rows, sortMetric]);

  const topConsumers = sorted.slice(0, 5);

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div style={{ height: 160, borderRadius: 12, background: 'var(--bg-muted)', marginBottom: 16 }} />
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[130, 60, 80, 90, 90, 70, 70]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cost guardrails</h1>
          <p className="page-subtitle">
            Storage, egress, and embed-view aggregates per hosted trust org, against a soft per-tier guardrail (D-21). Soft caps are a{' '}
            <strong>placeholder</strong> — no rate card ships in T1 (17_ §9 item 13); this is visibility, not enforcement.
          </p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="gauge"
        emptyTitle="No cost data yet"
        emptyBody="No daily rollup has landed via POST /telemetry/trust. This fills in once a hosted trust org has been provisioned and the platform's daily rollup job has run at least once."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
          <Kpi label="Embed views (latest)" value={kpis.totalEmbedViews.toLocaleString()} icon="eye" accent="#4F46E5" />
          <Kpi
            label="Storage reported"
            value={reportingStorage.length > 0 ? `${kpis.totalStorage.toLocaleString()} MB` : '—'}
            icon="hard-drive"
            hint={reportingStorage.length > 0 ? `${reportingStorage.length}/${rows.length} orgs reporting` : 'not yet instrumented (T2)'}
          />
          <Kpi label="Parse credits consumed" value={kpis.totalParseConsumed.toLocaleString()} icon="ticket" hint="metering dark until T2 (17_ §2.3)" />
          <Kpi
            label="Orgs over guardrail"
            value={kpis.overCount}
            icon="shield-alert"
            accent={kpis.overCount > 0 ? '#D97706' : undefined}
            hint="≥90% of soft cap"
          />
        </div>

        {reportingEgress.length === 0 && (
          <div style={{ padding: '10px 12px', background: 'var(--bg-muted)', borderRadius: 8, marginBottom: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>
            No org has reported egress yet. TPRM7's daily trust-telemetry-rollup sends it on every org row, so an org appears here after its next rollup (an org whose usage read failed sends nothing rather than 0). If this stays empty a day after the rollup ran, check that the rollup is running and reaching the licence server.
          </div>
        )}

        {kpis.overCount > 0 && (
          <div className="card" style={{ padding: 0, marginBottom: 18 }}>
            {overGuardrail.slice(0, 5).map((r) => {
              const tier = tierFor(r);
              const storagePct = typeof r.metrics.storage_mb === 'number' ? Math.round((r.metrics.storage_mb / STORAGE_SOFT_CAP_MB[tier]) * 100) : null;
              const embedPct = Math.round((r.metrics.embed_views / EMBED_VIEWS_SOFT_CAP[tier]) * 100);
              const which = storagePct !== null && storagePct >= embedPct ? `storage at ${storagePct}%` : `embed views at ${embedPct}%`;
              return (
                <AttnRow key={r.id} icon="shield-alert" color="#D97706" action="View org" onAction={() => navigate(r.license?.id ? `${base}/licenses/${r.license.id}` : `${base}/trust/org-metrics`)}>
                  <strong>{orgNameFor(r)}</strong> ({tierLabel(tier)}) is nearing its guardrail — {which} of the soft cap.
                </AttnRow>
              );
            })}
          </div>
        )}

        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="card-title">Top consumers</span>
            <select value={sortMetric} onChange={(e) => setSortMetric(e.target.value as SortMetric)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface)' }}>
              {(Object.keys(SORT_LABEL) as SortMetric[]).map((m) => <option key={m} value={m}>{SORT_LABEL[m]}</option>)}
            </select>
          </div>
          {topConsumers.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No orgs to rank yet.</div>
          ) : (
            <div style={{ padding: '4px 16px 12px' }}>
              {topConsumers.map((r) => {
                const tier = tierFor(r);
                const value = sortMetric === 'embed_views' ? r.metrics.embed_views : sortMetric === 'parse_consumed' ? r.metrics.parse_consumed : r.metrics.storage_mb;
                const cap = sortMetric === 'embed_views' ? EMBED_VIEWS_SOFT_CAP[tier] : sortMetric === 'storage_mb' ? STORAGE_SOFT_CAP_MB[tier] : null;
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: '0 0 200px', fontSize: 13, fontWeight: 500 }}>
                      {r.license?.id ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{orgNameFor(r)}</Link> : orgNameFor(r)}
                      <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>{tierLabel(tier)}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      {value == null ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>not yet reported</span>
                      ) : cap ? (
                        <UtilBar used={value} total={cap} />
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{value.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Org</th>
                  <th style={th}>Tier</th>
                  <th style={th}><Eye size={11} style={{ verticalAlign: -1 }} /> Embed views</th>
                  <th style={th}><HardDrive size={11} style={{ verticalAlign: -1 }} /> Storage</th>
                  <th style={th}><Ticket size={11} style={{ verticalAlign: -1 }} /> Parse credits</th>
                  <th style={th}>Last ingest</th>
                  <th style={th}><ShieldAlert size={11} style={{ verticalAlign: -1 }} /> Guardrail</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const tier = tierFor(r);
                  const embedPct = Math.round((r.metrics.embed_views / EMBED_VIEWS_SOFT_CAP[tier]) * 100);
                  const isOver = overGuardrail.some((o) => o.id === r.id);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={td}>
                        {r.license?.id
                          ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>{orgNameFor(r)}</Link>
                          : orgNameFor(r)}
                      </td>
                      <td style={{ ...td, color: 'var(--text-secondary)' }}>{tierLabel(tier)}</td>
                      <td style={td}>
                        {r.metrics.embed_views.toLocaleString()}
                        {!r.metrics.embed_enabled && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>(embed off)</span>}
                        <div style={{ marginTop: 3, width: 90 }}><UtilBar used={r.metrics.embed_views} total={EMBED_VIEWS_SOFT_CAP[tier]} height={4} /></div>
                      </td>
                      <td style={td}>
                        {typeof r.metrics.storage_mb === 'number' ? (
                          <div style={{ width: 90 }}><UtilBar used={r.metrics.storage_mb} total={STORAGE_SOFT_CAP_MB[tier]} height={4} /></div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>not yet reported</span>
                        )}
                      </td>
                      <td style={td}>{r.metrics.parse_consumed.toLocaleString()}</td>
                      <td style={td}>
                        <span style={{ color: 'var(--text-muted)' }}>{formatRelativeTime(r.ingested_at)}</span>
                      </td>
                      <td style={td}>
                        {isOver ? <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">≥90% cap</Pill> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>
    </div>
  );
}

export default CostGuardrailsPage;
